import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

const STATEMENT_MAX_LENGTH = 50
const EXPAND_AFTER_ROW_COUNT = 25

export default class extends Controller {
  static targets = ["list", "input", "inputWrapper"]

  declare readonly listTarget: HTMLElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly inputWrapperTarget: HTMLElement

  private formElement: HTMLFormElement | null = null
  private isSaving: boolean = false
  private formDirty: boolean = false

  connect() {
    this.formElement = this.element.closest("form") as HTMLFormElement
    this.formDirty = false
    this.isSaving = false
    document.addEventListener("book-slider:saveStatementVoting", this.saveOnSlideChange.bind(this))
    document.addEventListener("saveBeforeNavigate", this.saveBeforeNavigate.bind(this))
    this.tryAutoShowStatementInputOnLoad()
  }

  private tryAutoShowStatementInputOnLoad() {
    if ((this.element as HTMLElement).dataset.statementInputAutoOpen === "1") return
    const delays = [0, 80, 180]
    for (const delay of delays) {
      window.setTimeout(() => {
        if ((this.element as HTMLElement).dataset.statementInputAutoOpen === "1") return
        this.autoShowStatementInputIfPossible()
      }, delay)
    }
  }

  private autoShowStatementInputIfPossible() {
    const wrapper = this.inputWrapperTarget as HTMLElement | undefined
    if (!wrapper) return

    // Don't steal focus if the user already opened the input or typed something.
    if (wrapper.style.display !== "none") return
    if (this.inputTarget.value.trim().length > 0) return

    ;(this.element as HTMLElement).dataset.statementInputAutoOpen = "1"
    wrapper.style.display = "flex"
  }

  disconnect() {
    document.removeEventListener(
      "book-slider:saveStatementVoting",
      this.saveOnSlideChange.bind(this),
    )
    document.removeEventListener("saveBeforeNavigate", this.saveBeforeNavigate.bind(this))
  }

  private saveBeforeNavigate(event: Event) {
    const customEvent = event as CustomEvent<{ promises: Promise<unknown>[] }>
    if (!customEvent.detail?.promises) return
    if (!this.formDirty) return
    if (!this.hasAtLeastOneStatement() && !this.hasPendingDestroy()) return
    if (this.formElement) {
      customEvent.detail.promises.push(this.saveForm({ userTriggered: false }))
    }
  }

  private saveOnSlideChange(event: Event) {
    const customEvent = event as CustomEvent<{ leftSlide?: HTMLElement }>
    const leftSlide = customEvent.detail?.leftSlide
    if (leftSlide && !this.isElementInSlide(leftSlide)) return
    if (!this.formDirty) return
    if (!this.hasAtLeastOneStatement() && !this.hasPendingDestroy()) return
    this.saveForm({ userTriggered: false })
  }

  /** Walk parent/host chain so we find leftSlide even when this.element is inside a shadow root. */
  private isElementInSlide(slideEl: HTMLElement): boolean {
    let node: Node | null = this.element
    while (node) {
      if (node === slideEl) return true
      const root = node.getRootNode() as ShadowRoot
      node = node.parentNode ?? root?.host ?? null
    }
    return false
  }

  private hasPendingDestroy(): boolean {
    const form = this.formElement
    if (!form) return false
    const container = form.querySelector(".statement-input-pending-destroys")
    if (!container) return false
    return container.querySelectorAll('input[name*="[id]"]').length > 0
  }

  private hasAtLeastOneStatement(): boolean {
    const visible = this.getVisibleListItems()
    return visible.some((li) => {
      const statementField = li.querySelector('input[name*="[statement]"]') as HTMLInputElement
      return statementField?.value?.trim().length > 0
    })
  }

  private getVisibleListItems(): HTMLElement[] {
    return Array.from(this.listTarget.querySelectorAll("li.statement-tag-item")).filter(
      (li) => (li as HTMLElement).style.display !== "none" && li.parentNode !== null,
    ) as HTMLElement[]
  }

  private getVisibleStatementCount(): number {
    return this.getVisibleListItems().length
  }

  private isOnLastStatementVotingSlide(): boolean {
    const form = this.formElement
    if (!form) return false
    const allForms = document.querySelectorAll(
      '[data-slide-type^="statement-voting"] form.statement-voting-form',
    )
    if (allForms.length === 0) return false
    const lastForm = allForms[allForms.length - 1]
    return lastForm === form || lastForm.contains(this.element)
  }

  private showExpandLoader(): HTMLElement {
    const slide =
      this.element.closest(".slide") ||
      this.element.closest('[data-slide-type^="statement-voting"]')
    const container = slide || this.element
    if (!(container instanceof HTMLElement)) return document.createElement("div")

    const wrapper = document.createElement("div")
    wrapper.className = "statement-voting-expand-loader"
    wrapper.setAttribute("aria-live", "polite")
    wrapper.setAttribute("role", "status")
    wrapper.style.cssText =
      "position:absolute;inset:0;background:rgba(255,255,255,0.92);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1rem;z-index:20;border-radius:inherit;"
    wrapper.innerHTML = `
      <div class="spinner-border text-primary" role="status" style="width:2.5rem;height:2.5rem;">
        <span class="visually-hidden">Loading</span>
      </div>
      <p class="mb-0 small fw-medium text-secondary">Saving statements and adding new page…</p>
    `
    const style = container.style.position
    if (!style || style === "static") container.style.position = "relative"
    container.appendChild(wrapper)
    return wrapper
  }

  private preserveDestroyInForm(li: HTMLElement, statementId: string) {
    const form = this.formElement
    if (!form) return
    const statementField = li.querySelector('input[name*="[statement]"]') as HTMLInputElement
    if (!statementField) return
    const match = statementField.name.match(/\[statement_voting_statements_attributes\]\[(\d+)\]/)
    const index = match ? match[1] : "0"

    let container = form.querySelector(".statement-input-pending-destroys") as HTMLElement
    if (!container) {
      container = document.createElement("div")
      container.className = "statement-input-pending-destroys"
      container.setAttribute("aria-hidden", "true")
      container.style.display = "none"
      form.appendChild(container)
    }

    const idInput = document.createElement("input")
    idInput.type = "hidden"
    idInput.name = `book[statement_voting_statements_attributes][${index}][id]`
    idInput.value = statementId
    container.appendChild(idInput)

    const destroyInput = document.createElement("input")
    destroyInput.type = "hidden"
    destroyInput.name = `book[statement_voting_statements_attributes][${index}][_destroy]`
    destroyInput.value = "1"
    container.appendChild(destroyInput)
  }

  focusInput() {
    if (this.inputWrapperTarget) this.inputWrapperTarget.style.display = "flex"
    setTimeout(() => this.inputTarget.focus(), 10)
  }

  handleKey(event: KeyboardEvent) {
    const value = this.inputTarget.value.trim()

    if (event.key === "Enter") {
      if (!value.length) return
      event.preventDefault()

      this.addStatementBadge(value)
      this.inputTarget.value = ""
      setTimeout(() => this.inputTarget.focus(), 10)
    }
  }

  handleBlur() {
    const value = this.inputTarget.value.trim()

    if (value) {
      this.addStatementBadge(value)
      this.inputTarget.value = ""
      this.formDirty = true
    }

    if (this.inputWrapperTarget) {
      this.inputWrapperTarget.style.display = "none"
    }
  }

  removeStatement(event: Event) {
    event.preventDefault()
    event.stopPropagation()
    const button = event.currentTarget as HTMLElement
    const li = button.closest("li.statement-tag-item") as HTMLElement
    if (!li) return

    const idField = li.querySelector('input[name*="[id]"]') as HTMLInputElement

    if (idField?.value) {
      this.preserveDestroyInForm(li, idField.value)
      li.remove()
    } else {
      const statementField = li.querySelector('input[name*="[statement]"]') as HTMLInputElement
      const indexMatch = statementField?.name.match(
        /\[statement_voting_statements_attributes\]\[(\d+)\]/,
      )
      if (indexMatch) {
        const index = indexMatch[1]
        for (const el of this.listTarget.querySelectorAll(`input[name*="[${index}]"]`)) {
          el.remove()
        }
      }
      li.remove()
    }

    this.formDirty = true
  }

  addStatementBadge(statement: string) {
    const trimmed = statement.trim().slice(0, STATEMENT_MAX_LENGTH)
    if (!trimmed) return

    const existing = Array.from(this.listTarget.querySelectorAll("li.statement-tag-item span"))
      .map((span) => span.textContent?.trim().toLowerCase())
      .filter(Boolean)

    if (existing.includes(trimmed.toLowerCase())) {
      return
    }

    const visible = this.getVisibleListItems()
    const indices = new Set<number>()
    visible.forEach((li) => {
      const fields = li.querySelectorAll('input[name*="[statement_voting_statements_attributes]"]')
      fields.forEach((f) => {
        const match = (f as HTMLInputElement).name.match(/\[(\d+)\]/)
        if (match) indices.add(parseInt(match[1], 10))
      })
    })

    let nextIndex = 0
    while (indices.has(nextIndex)) nextIndex++

    const template = document.createElement("template")
    template.innerHTML = `
      <li class="statement-tag-item p-1.5 rounded-lg flex items-center gap-2 text-sm md:text-base text-gray-800 font-medium list-none pl-0 ml-0 shrink-0" style="background: #EEECF6;">
        <input type="hidden" name="book[statement_voting_statements_attributes][${nextIndex}][_destroy]" value="0" class="destroy-field">
        <input type="hidden" name="book[statement_voting_statements_attributes][${nextIndex}][statement]" class="statement-field">
        <span class="flex-1"></span>
        <button type="button" class="p-0 m-0 bg-transparent border-0 cursor-pointer flex items-center justify-center text-gray-500 hover:text-gray-800 shrink-0" title="" data-action="statement-input#removeStatement">
          <i class="bi bi-x-lg text-xs md:text-sm"></i>
        </button>
      </li>
    `

    const statementItem = template.content.firstElementChild as HTMLLIElement
    ;(statementItem.querySelector("input.statement-field") as HTMLInputElement).value = trimmed
    statementItem.querySelector("span.flex-1").textContent = trimmed
    this.listTarget.querySelector("li:last-of-type")?.before(statementItem)

    this.formDirty = true
    const visibleAfterAdd = this.getVisibleStatementCount()
    const onLastSlide = this.isOnLastStatementVotingSlide()
    const shouldExpand = visibleAfterAdd >= EXPAND_AFTER_ROW_COUNT || onLastSlide

    if (shouldExpand) {
      ;(
        window as Window & { b4mGoToLastStatementVotingAfterReload?: boolean }
      ).b4mGoToLastStatementVotingAfterReload = true
      const loaderEl = this.showExpandLoader()
      this.saveForm({ expandStatementSlide: true, loaderElement: loaderEl })
    }
  }

  private buildFormData(includePending = true): FormData {
    const form = this.formElement
    if (!form) return new FormData()

    const formData = new FormData()
    const originalFormData = new FormData(form)
    for (const [key, value] of originalFormData.entries()) {
      if (!key.includes("[statement_voting_statements_attributes]")) {
        formData.append(key, value)
      }
    }

    const visible = this.getVisibleListItems()
    const statementMap = new Map<string, { id?: string; statement?: string; destroy: string }>()

    for (const li of visible) {
      const idField = li.querySelector('input[name*="[id]"]') as HTMLInputElement
      const destroyField = li.querySelector('input[name*="[_destroy]"]') as HTMLInputElement
      const statementField = li.querySelector('input[name*="[statement]"]') as HTMLInputElement
      if (!statementField) continue
      const indexMatch = statementField.name.match(
        /\[statement_voting_statements_attributes\]\[(\d+)\]/,
      )
      if (indexMatch) {
        statementMap.set(indexMatch[1], {
          id: idField?.value || undefined,
          statement: statementField.value,
          destroy: destroyField?.value || "0",
        })
      }
    }

    if (includePending) {
      const pending = form.querySelector(".statement-input-pending-destroys")
      if (pending) {
        const idInputs = pending.querySelectorAll('input[name*="[id]"]')
        idInputs.forEach((idEl, i) => {
          statementMap.set(`pending_${i}`, {
            id: (idEl as HTMLInputElement).value,
            destroy: "1",
          })
        })
      }
    }

    let activeIndex = 0
    for (const [, data] of statementMap.entries()) {
      const hasId = data.id && data.id.trim() !== ""
      const isDestroyed = data.destroy === "1"

      if (hasId && isDestroyed && data.id) {
        formData.append(`book[statement_voting_statements_attributes][${activeIndex}][id]`, data.id)
        formData.append(
          `book[statement_voting_statements_attributes][${activeIndex}][_destroy]`,
          "1",
        )
        activeIndex++
      } else if (data.statement || hasId) {
        if (hasId && data.id) {
          formData.append(
            `book[statement_voting_statements_attributes][${activeIndex}][id]`,
            data.id,
          )
        }
        if (data.statement) {
          formData.append(
            `book[statement_voting_statements_attributes][${activeIndex}][statement]`,
            data.statement,
          )
        }
        formData.append(
          `book[statement_voting_statements_attributes][${activeIndex}][_destroy]`,
          "0",
        )
        activeIndex++
      }
    }

    return formData
  }

  private buildFormDataFromControls(form: HTMLFormElement): FormData {
    const formData = new FormData()
    const controls = form.querySelectorAll("input[name], select[name], textarea[name]")
    for (const el of controls) {
      const name = (el as HTMLInputElement).name
      if (!name) continue
      if (el instanceof HTMLInputElement && el.type === "file") {
        for (const file of el.files || []) formData.append(name, file)
      } else {
        formData.append(name, (el as HTMLInputElement).value)
      }
    }
    return formData
  }

  private saveForm(
    options: {
      userTriggered?: boolean
      expandStatementSlide?: boolean
      loaderElement?: HTMLElement | null
    } = {},
  ): Promise<void> {
    if (this.isSaving) return Promise.resolve()
    if (!this.hasAtLeastOneStatement() && !this.hasPendingDestroy()) return Promise.resolve()

    const form = this.formElement
    if (!form) return Promise.resolve()

    const expandStatementSlide = options.expandStatementSlide === true
    const loaderElement = options.loaderElement ?? null

    this.isSaving = true
    const formData = this.buildFormDataFromControls(form)
    if (expandStatementSlide) {
      formData.append("expand_statement_slide", "1")
    }

    return fetch(form.action, {
      method: form.method || "PATCH",
      body: formData,
      headers: {
        Accept: "text/vnd.turbo-stream.html, text/html",
        "X-CSRF-Token":
          document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "",
      },
    })
      .then(async (res) => {
        const html = await res.text()
        if (res.ok) {
          if (html.includes("turbo-stream")) {
            Turbo.renderStreamMessage(html)
          }
          const pending = form.querySelector(".statement-input-pending-destroys")
          pending?.replaceChildren()
        }
      })
      .catch((error) => {
        console.error("Error saving statements:", error)
      })
      .finally(() => {
        this.isSaving = false
        if (!expandStatementSlide) {
          this.formDirty = false
        }
        if (loaderElement?.parentNode) {
          loaderElement.remove()
        }
      })
  }
}
